import {PayloadAction} from '@reduxjs/toolkit'
import {getLogger} from './logging'
import {selectAppView} from '../store/slices/appViewSlice'
import {selectCartItems} from '../store/slices/shoppingCartSlice'
import {selectUser} from '../store/slices/userSlice'
import {RootState} from '../store/store'
import {selectSearchParams} from '../store/slices/searchParamsSlice'
import {ShoppingCartItem} from '../types'
import {
  calcCartPriceSummary,
  calcOfferPriceSummary
} from '../services/offerUtils'
import {getPromoCode} from './utils'
import {
  formatISODateTime,
  toLocalDateTime,
  toPropertyDateTime
} from './dateUtils'

declare global {
  interface Window {
    dataLayer: Record<string, any>[]
    gtag: (...args: any[]) => void
  }
}

export interface DataLayerEvent {
  event: string
  [key: string]: any
}

interface ShoppingCartServiceData {
  id: string
  name: string
  price: number
}

interface ShoppingCartItemData {
  location: string
  propertyId: string
  ratePlanId: string
  ratePlanName: string
  unitGroupId: string
  unitGroupName: string
  arrival: string | undefined
  departure: string | undefined
  nights: number
  guests: number
  services: ShoppingCartServiceData[]
  totalRoomCharges: number
  totalIncludedServices: number
  totalAdditionalServices: number
  totalTaxesAndFees: number
  totalPrepayment: number
  total: number
}

interface ShoppingCartItemsEvent extends DataLayerEvent {
  items: ShoppingCartItemData[]
}

interface ShoppingCartEvent extends DataLayerEvent {
  numberOfRooms: number
  totalRoomCharges: number
  totalIncludedServices: number
  totalAdditionalServices: number
  totalTaxesAndFees: number
  totalPrepayment: number
  total: number
}

const logger = getLogger('dataLayer')

/**
 * Initializes Google Analytics 4 (GA4) with the provided tracking ID.
 * This function should be called once at the start of your application.
 * @param googleAnalyticsId The GA4 tracking ID (e.g., G-XXXXXXXXXX)
 */
export function initializeGA4(googleAnalyticsId: string): void {
  if (!window.dataLayer) {
    window.dataLayer = []
  }
  // load gtag.js script
  if (!document.getElementById('ga4-script')) {
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`
    script.id = 'ga4-script'
    document.head.appendChild(script)
  }

  // define gtag function
  window.gtag = () => {
    window.dataLayer.push(arguments)
  }

  // initialize GA4
  window.gtag('js', new Date())
  window.gtag('config', googleAnalyticsId)
}

/**
 * Push a single data item to the GTM data layer
 * @param dataItem
 */
export function pushDataLayerEvent(event: DataLayerEvent): void {
  try {
    logger.info({
      message: 'Pushing data event to GTM data layer',
      data: {event}
    })
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(event)
  } catch (error) {
    logger.error({
      message: 'Error pushing data event to GTM data layer',
      data: {error, event}
    })
  }
}

/**
 * Push multiple data items to the GTM data layer
 * @param dataItems
 */
export function pushDataLayerEvents(events: DataLayerEvent[]): void {
  try {
    logger.info({
      message: 'Pushing data events to GTM data layer',
      data: events
    })
    window.dataLayer = window.dataLayer || []
    events.forEach(event => {
      window.dataLayer.push(event)
    })
  } catch (error) {
    logger.error({
      message: 'Error pushing data events to GTM data layer',
      data: {error, events}
    })
  }
}

/**
 * Syncs the app init event to the GTM data layer
 */
export function syncInit(): void {
  // get derbysoft tracking id from local storage if it exists
  const dsClickId = localStorage.getItem('whyhotel_dsclid')

  logger.debug({
    message: `Checking for DS Click ID in local storage`,
    data: {dsClickId}
  })

  if (dsClickId) {
    const event = {
      'event': 'app_init',
      'dsClickID': dsClickId,
      'DS Click ID': dsClickId
    }

    logger.debug({
      message: 'Pushing app_init event to GTM data layer',
      data: {event}
    })

    pushDataLayerEvent(event)
  }
}

/**
 * Syncs the set app view event to the GTM data layer
 * @param state The redux store state
 */
export function syncSetAppView(state: RootState): void {
  const appView = selectAppView(state)
  const event = {
    'event': 'app_view',
    appView
  }
  logger.debug({
    message: 'Pushing app_view event to GTM data layer',
    data: {event}
  })
  pushDataLayerEvent(event)
}

export function syncBeginCheckout(): void {
  const event = {
    'event': 'begin_checkout'
  }
  logger.debug({
    message: 'Pushing begin_checkout event to GTM data layer',
    data: {event}
  })
  pushDataLayerEvent(event)
}

export function syncViewSearchResults(): void {
  const event = {
    'event': 'view_item_list'
  }
  logger.debug({
    message: 'Pushing view_item_list event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}

/**
 * Syncs the add to cart event to the GTM data layer
 * @param action The redux action
 */
export function syncAddToCart(action: PayloadAction<ShoppingCartItem>): void {
  const {id, offer, propertyId, guests}: ShoppingCartItem = action.payload
  const event = {
    'event': 'add_to_cart',
    'itemId': id,
    'roomNights': offer?.timeSlices?.length || 0,
    'roomTypeId': offer?.unitGroup?.id,
    'roomTypeName': offer?.unitGroup?.name,
    'ratePlanName': offer?.ratePlan?.name,
    'ratePlanId': offer?.ratePlan?.id,
    'numOfAdults': guests,
    'hotel': propertyId
  }

  logger.debug({
    message: 'Pushing add_to_cart event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}

/**
 * Syncs the remove from cart event to the GTM data layer
 * @param action The redux action
 */
export function syncRemoveFromCart(action: PayloadAction<string>): void {
  const itemId = action.payload
  const event = {
    'event': 'remove_from_cart',
    itemId
  }

  logger.debug({
    message: 'Pushing remove_from_cart event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}

/**
 * Syncs the update cart item event to the GTM data layer
 * @param action The redux action
 */
export function syncUpdateCartItem(
  action: PayloadAction<ShoppingCartItem>
): void {
  const {id, offer, propertyId, guests}: ShoppingCartItem = action.payload

  const event = {
    'event': 'update_cart_item',
    'itemId': id,
    'roomNights': offer?.timeSlices?.length || 0,
    'roomTypeId': offer?.unitGroup?.id,
    'roomTypeName': offer?.unitGroup?.name,
    'ratePlanName': offer?.ratePlan?.name,
    'ratePlanId': offer?.ratePlan?.id,
    'numOfAdults': guests,
    'revenue': null,
    'ecommerce.purchase.actionField.revenue': null,
    'hotel': propertyId
  }

  logger.debug({
    message: 'Pushing update_cart_item event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}

/**
 * Syncs the set user event to the GTM data layer
 * @param state The redux store state
 */
export function syncSetUser(state: RootState): void {
  const user = selectUser(state)

  if (user?.isAuthenticated) {
    const event = {
      'event': 'login'
    }

    logger.debug({
      message: 'Pushing set_user event to GTM data layer',
      data: {event}
    })

    pushDataLayerEvent(event)
  }
}

/**
 * Syncs the show cart event to the GTM data layer
 * @param state The redux store state
 */
export function syncShowCart(): void {
  const event = {
    'event': 'view_cart'
  }

  logger.debug({
    message: 'Pushing show_cart event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}

/**
 * Syncs the submit search event to the GTM data layer
 * @param state The redux store state
 */
export function syncSubmitSearch(state: RootState): void {
  const searchParams = selectSearchParams(state)
  if (searchParams) {
    const propertyId =
      searchParams?.propertyId?.toLowerCase() === 'all'
        ? undefined
        : searchParams.propertyId

    const arrivalDate = searchParams?.dateRange?.start
      ? formatISODateTime(toLocalDateTime(searchParams.dateRange.start))
      : undefined

    const departureDate = searchParams?.dateRange?.end
      ? formatISODateTime(toLocalDateTime(searchParams.dateRange.end))
      : undefined

    const roomNights = searchParams?.dateRange?.nights || 0

    const event = {
      'event': 'submit_search',
      arrivalDate,
      departureDate,
      roomNights,
      'numOfAdults': searchParams?.guests,
      'numOfChildren': 0,
      'numOfGuests': searchParams?.guests,
      'hotel': propertyId,
      'promoCode': getPromoCode(searchParams.specialRates),
      'corporateCode': searchParams.specialRates?.corporateCode
    }

    logger.debug({
      message: 'Pushing submit_search event to GTM data layer',
      data: {event}
    })

    // fire search submission event
    pushDataLayerEvent(event)

    // fire events for weekly and monthly stay searches which show extended stay banners
    if (5 <= roomNights && roomNights < 20) {
      pushDataLayerEvent({
        event: 'Weekly_Stay_Search',
        category: 'IBE',
        value: 1
      })
    } else if (roomNights >= 20) {
      pushDataLayerEvent({
        event: 'Monthly_Stay_Search',
        category: 'IBE',
        value: 1
      })
    }
  }
}

export function syncBooking(action: PayloadAction<string>): void {
  const bookingId = action.payload
  const reservationId = `${bookingId}-1`
  const event = {
    'event': 'purchase',
    bookingId,
    reservationId,
    'dateMade': new Date().toISOString()
  }

  logger.debug({
    message: 'Pushing complete_booking event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}

/**
 * Syncs the shopping cart items to the GTM data layer
 * @param state The redux store state
 */
export function syncShoppingCart(state: RootState): void {
  const cartItems = selectCartItems(state)
  const shoppingCartItems: ShoppingCartItemData[] = cartItems.map(item => {
    const pricingSummary = calcOfferPriceSummary(
      item.offer,
      item.additionalServices
    )

    const services: ShoppingCartServiceData[] = item.additionalServices.map(
      additionalService => {
        return {
          id: additionalService?.service?.id,
          name: additionalService?.service?.name,
          price: additionalService?.totalAmount?.grossAmount
        }
      }
    )

    const arrivalDate = item.offer?.arrival
      ? formatISODateTime(toPropertyDateTime(item.offer.arrival))
      : undefined

    const departureDate = item.offer?.departure
      ? formatISODateTime(toPropertyDateTime(item.offer.departure))
      : undefined

    return {
      location: item.propertyId,
      propertyId: item.propertyId,
      ratePlanId: item.offer?.ratePlan?.id,
      ratePlanName: item.offer?.ratePlan?.name,
      unitGroupId: item.offer?.unitGroup?.id,
      unitGroupName: item.offer?.unitGroup?.name,
      arrival: arrivalDate,
      departure: departureDate,
      nights: item.offer?.timeSlices.length || 0,
      guests: item.guests,
      services,
      totalRoomCharges: pricingSummary.totalRoomCharges,
      totalIncludedServices: pricingSummary.totalIncludedServices,
      totalAdditionalServices: pricingSummary.totalAdditionalServices,
      totalTaxesAndFees: pricingSummary.totalTaxesAndFees,
      totalPrepayment: pricingSummary.totalPrepayment,
      total: pricingSummary.total
    }
  })

  const shoppingCartItemsEvent: ShoppingCartItemsEvent = {
    event: 'shopping_cart_items',
    items: shoppingCartItems
  }

  logger.debug({
    message: 'Pushing shopping_cart_items event to GTM data layer',
    data: {shoppingCartItemsEvent}
  })

  pushDataLayerEvent(shoppingCartItemsEvent)

  const cartPriceSummary = calcCartPriceSummary(cartItems)
  const shoppingCartEvent: ShoppingCartEvent = {
    event: 'shopping_cart',
    numberOfRooms: cartItems.length,
    totalRoomCharges: cartPriceSummary?.totalRoomCharges,
    totalIncludedServices: cartPriceSummary?.totalIncludedServices,
    totalAdditionalServices: cartPriceSummary?.totalAdditionalServices,
    totalTaxesAndFees: cartPriceSummary?.totalTaxesAndFees,
    totalPrepayment: cartPriceSummary?.totalPrepayment,
    total: cartPriceSummary?.total
  }

  logger.debug({
    message: 'Pushing shopping_cart event to GTM data layer',
    data: {shoppingCartEvent}
  })
  pushDataLayerEvent(shoppingCartEvent)
}

/**
 * Syncs the IBE session ID to the GTM data layer
 * @param sessionId The session ID
 */
export const syncIbeSessionId = (sessionId: string): void => {
  const event = {
    'event': 'ibe_session_id',
    sessionId
  }

  logger.debug({
    message: 'Pushing session_id event to GTM data layer',
    data: {event}
  })

  pushDataLayerEvent(event)
}
